import {NumberInput, Slider, FormControl, Label} from 'platform/components';
import {VStack, HStack, Box} from 'platform/foundation';

import {FieldValues, FieldPath, useController} from 'react-hook-form';

import {isNil} from 'ramda';

import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

interface FuelLevelInputProps<TFieldValues extends FieldValues = FieldValues> extends TestIdProps {
  name: FieldPath<TFieldValues>;
  control: FormControl<TFieldValues>;
  label?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
}

const sliderTicks = [
  {value: 0, label: '0'},
  {value: 25, label: '25'},
  {value: 50, label: '50'},
  {value: 75, label: '75'},
  {value: 100, label: '100'},
];

export function FuelLevelInput<TFieldValues extends FieldValues = FieldValues>(
  props: FuelLevelInputProps<TFieldValues>
) {
  const {field} = useController({name: props.name, control: props.control});

  const fuelLevel = field.value ?? 0;

  const handleChange = (value: number | null) => {
    let newValue = value;

    if (isNil(value)) {
      return;
    }

    if (value < 0) {
      newValue = 0;
    } else if (value > 100) {
      newValue = 100;
    }

    field.onChange(newValue);
  };

  return (
    <VStack spacing={0}>
      <Label isRequired={props.isRequired}>
        {props.label ?? i18n.t('entity.workshop.labels.fuelLevel')}
      </Label>
      <HStack spacing={2}>
        <Box maxWidth={25}>
          <NumberInput
            value={fuelLevel}
            decimalPlaces={0}
            minStepperValue={0}
            maxStepperValue={100}
            onChange={handleChange}
            isDisabled={props.isDisabled}
            data-testid={suffixTestId('numberInput', props)}
          />
        </Box>
        <Box flex={1} width="100%">
          <Slider
            min={0}
            max={100}
            step={1}
            ticks={sliderTicks}
            value={fuelLevel}
            onChange={handleChange}
            isDisabled={props.isDisabled}
            data-testid={suffixTestId('slider', props)}
          />
        </Box>
      </HStack>
    </VStack>
  );
}
